// GanttDndContext.jsx - Context for managing drag and drop state using native HTML5 API
import React, { createContext, useState, useCallback } from 'react';

export const GanttDndContext = createContext(null);

export const useGanttDnd = () => {
  const context = React.useContext(GanttDndContext);
  if (!context) {
    throw new Error('useGanttDnd must be used within a GanttDndProvider');
  }
  return context;
};

export const GanttDndProvider = ({ children, onTaskMove }) => {
  const [draggingTask, setDraggingTask] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  const handleTaskMove = useCallback((data) => {
    if (onTaskMove) {
      onTaskMove(data);
    }
  }, [onTaskMove]);

  const toggleEditMode = useCallback(() => {
    setIsEditMode(prev => !prev);
    setSelectedTask(null);
  }, []);

  // Click-to-move: select a task, then click a different category row to move it there
  const selectTaskForMove = useCallback((task, category) => {
    setSelectedTask((prev) => (prev && prev.task.id === task.id ? null : { task, category }));
  }, []);

  const moveSelectedTaskTo = useCallback((toCategory) => {
    if (!selectedTask) return;
    if (selectedTask.category.id !== toCategory.id) {
      handleTaskMove({
        task: selectedTask.task,
        fromCategory: selectedTask.category,
        toCategory,
      });
    }
    setSelectedTask(null);
  }, [selectedTask, handleTaskMove]);

  const value = {
    draggingTask,
    setDraggingTask,
    onTaskMove: handleTaskMove,
    isEditMode,
    setIsEditMode,
    toggleEditMode,
    selectedTask,
    selectTaskForMove,
    moveSelectedTaskTo,
  };

  return (
    <GanttDndContext.Provider value={value}>
      {children}
    </GanttDndContext.Provider>
  );
};
